import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  createExecutiveReportViewLink,
  getExecutiveReportRun,
} from "../../api/executiveReportService";
import { getAccountInfo } from "../../utils/authToken";
import {
  EXECUTIVE_REPORT_JOB_COMPLETED,
  EXECUTIVE_REPORT_JOBS_CHANGED,
  getExecutiveReportJobs,
  removeExecutiveReportJob,
} from "../../utils/executiveReportJobs";
import useToast from "./toast/useToast";

const READY_STATUSES = new Set(["html_ready", "email_sent", "pdf_generating", "pdf_ready"]);

export default function ExecutiveReportJobTracker() {
  const { t } = useTranslation("commonTranslation");
  const { showSuccessToast, showErrorToast } = useToast();
  const processing = useRef(new Set());

  useEffect(() => {
    let active = true;

    const completeJob = async (job, run) => {
      if (processing.current.has(job.runId)) return;
      processing.current.add(job.runId);
      try {
        const response = await createExecutiveReportViewLink(job.runId);
        if (!active) return;
        removeExecutiveReportJob(job.runId);
        showSuccessToast(
          <span>
            {t("settings.reports.messages.ready")} {"—"}{" "}
            <a className="executive-report-toast-link" href={response.url} target="_blank" rel="noopener noreferrer">
              {t("settings.reports.actions.viewLatest")}
            </a>
          </span>,
          { life: 12000 }
        );
        window.dispatchEvent(new CustomEvent(EXECUTIVE_REPORT_JOB_COMPLETED, {
          detail: { runId: job.runId, subscriptionId: job.subscriptionId, run },
        }));
      } catch {
        // Keep the job so link creation can be retried on the next poll.
      } finally {
        processing.current.delete(job.runId);
      }
    };

    const poll = async () => {
      const accountId = getAccountInfo()?.accountId;
      if (!accountId) return;
      const jobs = getExecutiveReportJobs(accountId);
      await Promise.all(jobs.map(async (job) => {
        if (Date.now() - Number(job.createdAt || 0) > 24 * 60 * 60 * 1000) {
          removeExecutiveReportJob(job.runId);
          return;
        }
        try {
          const response = await getExecutiveReportRun(job.runId);
          const run = response.data;
          if (run?.status === "failed") {
            removeExecutiveReportJob(job.runId);
            showErrorToast(t("settings.reports.messages.failed"));
            window.dispatchEvent(new CustomEvent(EXECUTIVE_REPORT_JOB_COMPLETED, {
              detail: { runId: job.runId, subscriptionId: job.subscriptionId, run },
            }));
          } else if (run?.htmlS3Key || READY_STATUSES.has(run?.status)) {
            await completeJob(job, run);
          }
        } catch {
          // Temporary API errors are retried while the job remains tracked.
        }
      }));
    };

    poll();
    const interval = window.setInterval(poll, 5000);
    window.addEventListener(EXECUTIVE_REPORT_JOBS_CHANGED, poll);
    window.addEventListener("storage", poll);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener(EXECUTIVE_REPORT_JOBS_CHANGED, poll);
      window.removeEventListener("storage", poll);
    };
  }, [showErrorToast, showSuccessToast, t]);

  return null;
}
